// External imports
import React, { useState, useEffect } from 'react';
import { Routes, Route, useLocation, useNavigate } from 'react-router-dom';
import { CssBaseline, ThemeProvider as MuiThemeProvider, Container } from '@mui/material';
import Cookies from 'js-cookie';

// Local imports
import HomePage from './pages/Home';
import LogIn from './pages/LogIn';
import SignUp from './pages/SignUp';
import Dashboard from './pages/Dashboard';
import theme from './theme';
import Admin from './pages/HackerLogin';
import Support from './pages/Support';
import Hacker from './pages/Hacker';
import Tos from './pages/Tos';
import Faq from './pages/Faq';
import Legal from './pages/Legal';
import Privacy from './pages/Privacy';
import Settings from './pages/Settings';
import { useAppDispatch, useAppSelector } from './redux/hooks';
import { setAccountType, setAccountInfo } from './redux/features/general/meta';

// Routes
import { verifyUserAccess } from './routes/general/authentication';
import publicRoutes from './util/navigation/routes/publicRoutes';

// Context import
import { ThemeProvider } from './themeContext';
import AccessRequest from './pages/AccessRequest';
import clearCookies from './util/storage/clearCookies';
import clearLocalStorage from './util/storage/clearLocalStorage';
// import Template from './pages/Template'; // This page was deleted
import { setColors } from './redux/features/general/theme';
import lightTheme from './styles/themes/lightTheme';
import darkTheme from './styles/themes/darkTheme';
import ConsultationNote from './pages/ConsultationNote';
import NoteMicrophone from './pages/NoteMicrophone';
import Camera from './pages/Camera';
import LockScreen from './pages/LockScreen';
import { getDoctorPreferences } from './routes/doctor/patients';
import Consultation from './pages/Consultation';
import GroupSelection from './pages/GroupSelection';
import { setGroupAccessList } from './redux/features/auth/login';
import Group from './pages/Group';
import { setIsLockScreenWarningPopupOpened } from './redux/features/general/navigation';
import { getGroupAccessWithDoctorId } from './routes/doctor/account';
import { setGroupAccess } from './redux/features/doctor/group/group';

function App() {

const dispatch = useAppDispatch();
const navigate = useNavigate();
const location = useLocation();

const isRecording = useAppSelector(state => state.consultationNote.isRecording);
const lockScreen = useAppSelector(state => state.doctorPreferences.lockScreen);

const accountInfo = useAppSelector(state => state.meta.accountInfo);
const accountType = useAppSelector(state => state.meta.accountType);

const [currentPath, setCurrentPath] = useState('');
const [timeLeft, setTimeLeft] = useState(600);

document.addEventListener('keydown', function(e){
if (!process.env.REACT_APP_BACKEND_URL?.includes('https') || location.pathname.includes('/hacker')) { 
  return; 
}; 

if (e.ctrlKey && e.altKey && (e.key == 'l')) { 

  if (location.pathname != '/lock/screen') { 
    localStorage.setItem('lockScreenRedirect', location.pathname); 
  }; 

  localStorage.setItem('isScreenLocked', 'on'); 

  navigate('/lock/screen'); 

};
});

// --

document.addEventListener('mousedown', function(e){
setTimeLeft(600);
});

// --

const getAccount = async (type: string) => {
const isScreenLocked = localStorage.getItem('isScreenLocked');

if (isScreenLocked === 'on') { 
  navigate('/lock/screen'); 
  return; 
};

const res = await verifyUserAccess(type);

if (res?.data.status === 200) {

  dispatch(setAccountType(res.data.accountType));
  dispatch(setAccountInfo(res.data.account)); 

  if (res.data.accountType === 'doctor') { 

    document.cookie = `jwt=${res.data.token}; path=/`;

    if (!currentPath || (currentPath === '/')) { 

      navigate('/dashboard');

    };

  } else if (res.data.accountType === 'hacker') { 

    document.cookie = `adminJwt=${res.data.token}; path=/`;

    if (!currentPath.startsWith('/hacker')) {
        navigate('/hacker'); 
    }

  }; 
  
} else if (res.data.status === 202) { 

  if (res.data.accountType === 'doctor') { 

    dispatch(setAccountType(res.data.accountType));
    dispatch(setAccountInfo(res.data.account)); 
    dispatch(setGroupAccessList(res.data.groupAccessList)); 

    document.cookie = `jwt=${res.data.token}; path=/`;

    navigate('/login/group/auth/select'); 

  }; 
  
} else { 

  clearCookies();
  clearLocalStorage(); 

  window.location.reload();

  navigate('/login'); 

};
};

// Simplified handleLockScreenRedirection: always navigates to lock screen
const handleLockScreenRedirection = () => {
if (location.pathname !== '/lock/screen' && !location.pathname.startsWith('/group')) {
localStorage.setItem('lockScreenRedirect', location.pathname);
}
localStorage.setItem('isScreenLocked', 'on');
navigate('/lock/screen');
};

// --

const getPersonalGroupAccess = async () => {
if (accountInfo?._id && (accountType == 'doctor')) { 

  const res = await getGroupAccessWithDoctorId(accountInfo?._id); 

  if (res?.data.status === 200) { 

    dispatch(setGroupAccess(res.data.groupAccess)); 

  }; 

};
};

// --

useEffect(() => {
setCurrentPath(location.pathname);
},[location.pathname]);

// --

useEffect(() => {
const savedTheme = localStorage.getItem('theme');

dispatch(setColors(lightTheme));
},[]);

// --

useEffect(() => {
if (accountInfo) { 

  getPersonalGroupAccess(); 

};
},[accountInfo]);

// --

useEffect(()=>{
let myInterval = setInterval(() => {

    if (timeLeft > 0) {

        setTimeLeft(timeLeft - 20);

    }; 

    // -- 

    if ((timeLeft <= 310) && (timeLeft >= 290) && !isRecording) { 

      dispatch(setIsLockScreenWarningPopupOpened(true)); 

    }; 

    // -- 

    if (timeLeft === 0) { 

      if (lockScreen?.isEnabled) { 

        if (!location.pathname.includes('/hacker') && 
          !isRecording && 
          !location.pathname.includes('/group') && 
          !location.pathname.includes('consultation/note/mic/')) { 

          dispatch(setIsLockScreenWarningPopupOpened(false)); 

          handleLockScreenRedirection(); 
          
        };

      }; 
      
    }; 

    if (timeLeft === 0) {

        if (timeLeft === 0) {
            clearInterval(myInterval)
        };
    }; 

}, 20000); 

return ()=> {
    clearInterval(myInterval);
};
},[timeLeft, lockScreen, isRecording, location.pathname]);

// --

useEffect(() => {
if (currentPath && publicRoutes) { 

  const isPublicRoute = publicRoutes.some(route =>
    typeof route === 'string' ? currentPath == route : route.test(currentPath)
  );

  // Temporary FIX for the mic consultation route. 

  if ((!isPublicRoute) && (!currentPath.includes('consultation/note/mic')) && (!currentPath.includes('login/mobile')) && (!currentPath.includes('camera')) && (!currentPath.startsWith('/login/group'))) {
    
    const jwt = Cookies.get('jwt') as string;
    const adminJwt = Cookies.get('adminJwt') as string;

    if (jwt && (currentPath !== '/hacker')) {

      getAccount('doctor');

    } else if (adminJwt) {

      getAccount('hacker');

    } else {

      navigate('/login');

    };

  };

};
}, [currentPath]);

// --

return (
  <ThemeProvider>
  <MuiThemeProvider theme={theme}>
  <CssBaseline />
  <Container>
  <Routes>
  <Route path="/" element={<HomePage />} />
  <Route path="login" element={<LogIn />} />
  <Route path="login/group/:groupAccessId" element={<LogIn />} />
  <Route path="login/group/auth/select" element={<GroupSelection />} />
  <Route path="signup" element={<SignUp />} />
  <Route path="signup/:referralLinkId" element={<SignUp />} />
  <Route path="dashboard">
  <Route index element={<Dashboard />} />
  <Route path=":section" element={<Dashboard />} />
  <Route path=":section/:templateId" element={<Dashboard />} />
  </Route>
  <Route path="consultation/:consultationId/note/:consultationNoteId" element={<Consultation />} />
  <Route path="hacker/login" element={<Admin />} />
  <Route path="hacker">
      <Route index element={<Hacker />} />
      <Route path=":section" element={<Hacker />} />
  </Route>
  <Route path="support" element={<Support />} />
  {/* FIX: Updated settings route to handle sections */}
  <Route path="settings">
      <Route index element={<Settings />} />
      <Route path=":section" element={<Settings />} />
  </Route>
  <Route path="tos" element={<Tos />} />
  <Route path="privacy" element={<Privacy />} />
  <Route path="faq" element={<Faq />} />
  <Route path="legal" element={<Legal />} />
  <Route path='patient/access/request/:patientAccessRequestId' element={<AccessRequest />} />
  <Route path='consultation/note/:consultationId/:consultationNoteId' element={<ConsultationNote />} />
  <Route path='consultation/note/template/:consultationId/:consultationNoteId' element={<ConsultationNote />} />
  <Route path='consultation/note/mic/:consultationId/:consultationNoteId/:securityPin' element={<NoteMicrophone />} />
  <Route path='camera/:consultationId/:securityPin' element={<Camera />} />
  <Route path='lock/screen' element={<LockScreen />} />
  {/* FIX: Updated group routes to handle sections and special cases */}
  <Route path="group">
      <Route index element={<Group />} />
      <Route path="intent/:setupIntentId" element={<Group />} />
      <Route path=":section" element={<Group />} />
  </Route>
  </Routes>
  </Container>
  </MuiThemeProvider>
  </ThemeProvider>
);
}

export default App;